import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { GenerationResultDto } from '@tinct/core-api-models';
import { ApiError, tinctApi } from '../../api';

/** Backend error code of a refused regeneration: the workspace lacks the generation credits. */
export const GENERATION_QUOTA_EXCEEDED_ERROR_CODE = 5105;

export interface RegenerateOutdatedParams {
  id: string;
  organizationId: string;
}

export const regenerateOutdated = async (params: RegenerateOutdatedParams): Promise<GenerationResultDto> => {
  return await tinctApi.campaigns.regenerateOutdated(params.id);
};

/**
 * Regenerates the pages of a campaign flagged `needs_regeneration`. Same invalidations as
 * generate-all: the campaign (flags and counts), the lists, and the organization (credits).
 */
export const useRegenerateOutdated = (
  options?: Omit<
    UseMutationOptions<GenerationResultDto, ApiError, RegenerateOutdatedParams>,
    'mutationFn'
  >,
) => {
  const { onSuccess, ...restOptions } = options || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: regenerateOutdated,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', args[1].id] });
      queryClient.invalidateQueries({ queryKey: ['organization', args[1].organizationId] });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
};
